import { PermissionCheck, Permissions } from '@firebird-web/acl';
import { continents } from '@firebird-web/shared-constants';
import { DropdownOption } from 'libs/shared/interfaces/src/lib';
import { ModelWeightedForecastTypeEnum } from 'libs/shared/enums';
import {
  ForecastTypeList,
  SecondaryLensList,
  WeightedFilter,
  WeightedLensList,
} from '../../../../forecast/src/lib/modules/overview/enums';
import { NavigationChip } from '../../../../shared/interfaces/src/lib/navigation-chips.interface';
import {
  IsoNaRegionType,
  WeightedPrimaryLensType,
  WeightedTabKey,
} from '../enums';
import { NaWidgetRegionTypeDataSource } from '../interfaces';
import { WeightedLegendBarConfig } from '../interfaces/legends.type';
import { IsoGraphV2Parameter } from '../interfaces/weighted-model-forecast.interface';

export const NUMBER_OF_DAYS = 15;
export const TWELVE_HOURS_IN_MS = 12 * 1000 * 60 * 60;

export const NaIsoContinent = 'NA';
export const CurrentTypeLegend = 'current';
export const PreviousTypeLegend = 'previous';
export const NormalTypeLegend = 'normal';
export const NormalsSeries = 'normals';
export const LegendIcons = [
  'thick-line',
  'medium-line',
  'line',
  'three-dashes-line',
  'thick-dashed-line',
  'vertical-dashed-line',
  'short-dashed-line',
  'short-dashed-line',
];
export const LegendMarketFirstIcons = ['thick-line', 'medium-line'];

export const WEIGHTED_ISO_COUNTRY_FORECAST_PERMISSIONS = [
  'Weighted_Forecasts',
  'ISO-Country',
];

export const WEIGHTED_DEGREE_FORECAST_PERMISSIONS = [
  'Weighted_Forecasts',
  'Degree_Day',
];

export const EXTENDED_DEGREE_DAYS_FORECAST_PERMISSIONS = [
  'Sub-Seasonal_Forecasts',
];

export const EXTENDED_DEGREE_DAYS_FORECAST_POOLSET_PERMISSIONS = [
  ['WF-NA'],
  ['WF-EUR'],
];

export const EXTENDED_DEGREE_DAYS_FORECAST_NA_PERMISSIONS = [
  'Sub-Seasonal_Forecasts',
];

export const WEIGHTED_US_GAS_DEMAND_FORECAST_PERMISSIONS = [
  'Weighted_Forecasts',
  'BCF',
];

export type WeightedTabConfig = {
  path: WeightedTabKey;
  labelKey: string;
  permissions?: Permissions;
  permissionChecks?: PermissionCheck[];
};

export const WEIGHTED_TAB_CONFIG: WeightedTabConfig[] = [
  {
    path: WeightedTabKey.Iso,
    labelKey: 'iso',
    permissions: WEIGHTED_ISO_COUNTRY_FORECAST_PERMISSIONS,
  },
  {
    path: WeightedTabKey.IsoExtended,
    labelKey: 'isoExtended',
    permissions: [EXTENDED_DEGREE_DAYS_FORECAST_PERMISSIONS],
  },
  {
    path: WeightedTabKey.Degree,
    labelKey: 'degree',
    permissions: WEIGHTED_DEGREE_FORECAST_PERMISSIONS,
  },
  {
    path: WeightedTabKey.DegreeExtended,
    labelKey: 'degreeExtended',
    permissionChecks: [
      { permissions: ['Sub-Seasonal_Forecasts'], key: 'View' },
      { permissions: ['Weighted_Forecasts', 'Degree_Day'], key: 'View' },
      {
        permissions: EXTENDED_DEGREE_DAYS_FORECAST_POOLSET_PERMISSIONS,
        key: 'PoolSet',
      },
    ],
  },
  {
    path: WeightedTabKey.GasDemand,
    labelKey: 'gasDemand',
    permissions: WEIGHTED_US_GAS_DEMAND_FORECAST_PERMISSIONS,
  },
];

export const WeightedNavigationTab: NavigationChip[] = WEIGHTED_TAB_CONFIG.map(
  ({ labelKey, path, permissions, permissionChecks }) => ({
    labelKey,
    value: [path],
    permission: permissions,
    permissionChecks,
  })
);

const ALL_WEIGHTED_TABS = [
  WeightedTabKey.Iso,
  WeightedTabKey.IsoExtended,
  WeightedTabKey.Degree,
  WeightedTabKey.DegreeExtended,
];

export const ContinentList = [
  {
    text: 'North America',
    id: continents.NORTH_AMERICA,
    permission: ['WF-NA'],
    tabs: ALL_WEIGHTED_TABS,
  },
  {
    text: 'Europe',
    id: continents.EUROPE,
    permission: ['WF-EUR'],
    tabs: ALL_WEIGHTED_TABS,
  },
  {
    text: 'Asia',
    id: continents.ASIA,
    permission: ['WF-ASIA'],
    // Extended (EC46) forecasts aren't available for Asia yet.
    // TODO: re-enable Degree once Asia has 10 and 30 year normals.
    tabs: [WeightedTabKey.Iso, WeightedTabKey.IsoExtended],
  },
  {
    text: 'Mexico',
    id: continents.MEXICO,
    permission: ['WF-MEX'],
    // Weighted Degree Day forecasts (regular or EC46/extended) aren't available for Mexico yet.
    tabs: [WeightedTabKey.Iso, WeightedTabKey.IsoExtended],
  },
];

export const ContinentListWDD = ContinentList.filter((continent) =>
  continent.tabs.includes(WeightedTabKey.Degree)
);

export const ContinentListWDDExtended = ContinentList.filter((continent) =>
  continent.tabs.includes(WeightedTabKey.DegreeExtended)
);

export const WEIGHTED_CONTINENT_PERMISSION_KEY = 'PoolSet';

export const WEIGHTED_MF_MODEL_PERMISSIONS = ['MarketFirst'];
export const WEIGHTED_FE_PERMISSION = ['ForecastEdge'];
export const FE_MODEL = 'MF_ECMWF_ENS';

// Standardized identifiers for model families
export const MARKET_FIRST_PREFIX = 'MF_';
export const MARKET_FIRST_TOKEN = 'MF';
export const FORECAST_EDGE_PREFIX = 'FE_';
export const FORECAST_EDGE_TOKEN = 'FE';

// Display prefixes for forecast types (with trailing space for UI display)
export const FORECAST_DISPLAY_PREFIX = {
  MARKET_FIRST: 'MF ',
  FORECAST_EDGE: 'FE ',
} as const;

export const modelDaysMapping: Record<string, number> = {
  ECMWF_OP: 10,
  ECMWF_OP_SHORT: 3,
  GEM_OP: 10,
  ECMWF_ENS: 15,
  ECMWF_ENS_SHORT: 6,
  GEM_ENS: 15,
  GFS_OP: 15,
  GFS_ENS: 15,
  ECMWF_AIFS: 15,
  ECMWF_AIFS_ENS: 15,
};

export const WEIGHTED_WS_MESSAGE_MODEL = 'CFAN';

export const WEIGHTED_ISO_COUNTRY_FORECAST_WS_MESSAGE_DATA_TABLE = [
  'model_aggregated_period_fcst',
  'aggregated_period_fcst',
];

export const WEIGHTED_DEGREE_FORECAST_WS_MESSAGE_DATA_TABLE =
  'fuel_weighted_forecasts';

export const WEIGHTED_CONTINENT_QUERY_PARAM = 'weightedContinent';

export const TableHeightConfig = {
  [WeightedPrimaryLensType.AG2]: 270,
  [WeightedPrimaryLensType.Model]: 370,
};

export const ChartHeightConfig = {
  [WeightedPrimaryLensType.AG2]: 0,
  [WeightedPrimaryLensType.Model]: 675,
};

export const IsoCountryNormalsLabels: {
  [key: string]: { label: string; normalType: string };
} = {
  AvgYrThirty: { label: '(30yr Avg)', normalType: ' Normals' },
  AvgYrTen: { label: '(10yr Avg)', normalType: ' Normals' },
  LastYear: { label: '(Last Year Obs)', normalType: '' },
};

export const NOT_AVAILABLE = '-';
export const NOT_AVAILABLE_VALUE = -888;
export const NOT_APPLICABLE = 'n/a';
export const NOT_APPLICABLE_VALUE = -999;
export const NOT_AVAILABLE_VALUES = [
  NOT_AVAILABLE_VALUE,
  '-888.0',
  '-888/-888',
  '-888.0/-888.0',
];
export const NOT_APPLICABLE_VALUES = [
  NOT_APPLICABLE_VALUE,
  '-999.0',
  '-999/-999',
  '-999.0/-999.0',
];

export const SHIMMER_COLOR_MAP: Record<string, string> = {
  '#f4f5f6': '#CFD0D1',
  '#f7dcca': '#D2BBAC',
  '#e8a988': '#C59074',
  '#c76753': '#D58D7E',
  '#a32a31': '#BA5F65',
  '#d5e4ef': '#B5C2CB',
  '#9dc3db': '#85A6BA',
  '#5791bf': '#81ADCF',
  '#3464a7': '#678BBD',
};
export const DEFAULT_SHIMMER_COLOR = '#CFD0D1';

export const DefaultLegendPosition = {
  bottom: '25px',
};
export const defaultOptionGasConsumption = {
  forecastType: ForecastTypeList.Weighted,
  filter: WeightedFilter.GasDemand,
};
export const defaultOptionIsoForecast = {
  forecastType: ForecastTypeList.Weighted,
  filter: WeightedFilter.IsoCountryForecast,
};
export const defaultOptionDegreeDaysForecast = {
  forecastType: ForecastTypeList.Weighted,
  filter: WeightedFilter.WeightedDegreeDays,
};

export const DegreeDaysLegendConfig: WeightedLegendBarConfig[] = [
  {
    ...defaultOptionDegreeDaysForecast,
    primaryLens: WeightedLensList.AgDDaysDiff,
  },
  {
    ...defaultOptionDegreeDaysForecast,
    primaryLens: WeightedLensList.AgTemperature,
    secondaryLens: SecondaryLensList.AvgYrThirty,
  },
  {
    ...defaultOptionDegreeDaysForecast,
    primaryLens: WeightedLensList.MarketFirstDifference,
  },
];

export const DegreeDaysExtendedLegendConfig: WeightedLegendBarConfig[] = [
  {
    ...defaultOptionDegreeDaysForecast,
    primaryLens: WeightedLensList.AgDDaysDiff,
  },
  {
    ...defaultOptionDegreeDaysForecast,
    primaryLens: WeightedLensList.AgTemperature,
    secondaryLens: SecondaryLensList.AvgYrThirty,
  },
];

export const IsoCountryLegendConfig: WeightedLegendBarConfig[] = [
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.AgTemperature,
    secondaryLens: SecondaryLensList.AvgYrThirty,
  },
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.AgHddSdd,
  },
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.AgFeelsLike,
  },
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.AgTempDiff,
  },
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.AgDDaysDiff,
  },
  {
    ...defaultOptionIsoForecast,
    primaryLens: WeightedLensList.MarketFirstDifference,
  },
];

export const GasDemandLegendConfig: WeightedLegendBarConfig[] = [
  {
    ...defaultOptionGasConsumption,
    primaryLens: WeightedLensList.ConsumptionDaily,
  },
  {
    ...defaultOptionGasConsumption,
    primaryLens: WeightedLensList.ConsumptionPeriod,
  },
];

export const NaIsoRegionTypeDataSource: NaWidgetRegionTypeDataSource[] = [
  {
    labelKey: 'ISO Regions',
    value: IsoNaRegionType.Iso,
    permission: ['Weighted_Forecasts', 'ISO-Country'],
  },
  {
    labelKey: 'EIA Regions',
    value: IsoNaRegionType.Eia,
    permission: ['Weighted_Forecasts', 'Degree_Day'],
  },
];

export const CONTINENTS_WITHOUT_PWDD = [continents.EUROPE, continents.ASIA];

export const CONTINENTS_WITH_PWDD_TOGGLE = [
  continents.NORTH_AMERICA,
  continents.MEXICO,
];

export const MIN_MAX_TEMP_TAB = 'minMaxTemp';

export const NORMALS_LABELS = {
  AvgYrThirty: '30 yr Avg',
  AvgYrTen: '10 yr Avg',
  LastYear: 'Last Year Obs',
  Normal: 'Normal',
};

export const NORMALS_ROW_LABELS: Record<SecondaryLensList, string> = {
  [SecondaryLensList.AvgYrThirty]: 'Normals (30yr Avg)',
  [SecondaryLensList.AvgYrTen]: 'Normals (10yr Avg)',
  [SecondaryLensList.LastYear]: 'Last Year Obs',
};

export const OBS_LABEL = 'Observations';

export const EC46_WEEKS: DropdownOption[] = [
  {
    value: 'Week1&2',
    labelKey: 'Week 1 & 2',
  },
  {
    value: 'Week3&4',
    labelKey: 'Week 3 & 4',
  },
  {
    value: 'Week5&6',
    labelKey: 'Week 5 & 6',
  },
];

export const MODEL_FORECAST_OPTIONS_BY_DD = {
  HDD: [
    'Gas Weighted HDD',
    'Pop Weighted HDD',
    'Electric Weighted HDD',
    'Oil Weighted HDD',
  ],
  CDD: ['Pop Weighted CDD', 'Electric Weighted CDD'],
};

export const FORECAST_TYPE_TO_API_PARAMETER: Partial<
  Record<ModelWeightedForecastTypeEnum, IsoGraphV2Parameter>
> = {
  [ModelWeightedForecastTypeEnum.TEMPERATURE_DETAILS]: 'tempHourly',
  [ModelWeightedForecastTypeEnum.CLOUD_COVER_DETAILS]: 'cloudCover',
  [ModelWeightedForecastTypeEnum.WIND_SPEED_DETAILS]: 'wind',
  [ModelWeightedForecastTypeEnum.PRECIPITATION_DETAILS]: 'precip',
};
